import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import IconButtonAddContent from "../../../Buttons/IconButtonAddContent";
import CardMedia from "../../../Media/CardMedia";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { useFormContext } from "react-hook-form";
import { formatMediaUrl } from "../../../../services/adsCampaigns";
import {
  MAX_CONTENTS_TIKTOK_CAROUSEL,
  TIKTOK,
  TIKTOK_IMAGE_FORMATS,
} from "../../../../utils/constants";
import { getUUIDFromURL } from "../../../../utils/string";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { PencilLine, Plus, Trash2 } from "lucide-react";
import { IconButton } from "@mui/material";
import Button from "../../../Buttons/Button";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CAROUSEL_IMAGES_MANAGER_ADD_IMAGE,
  CAROUSEL_IMAGES_MANAGER_DELETE_ALL,
  CAROUSEL_IMAGES_MANAGER_NO_IMAGES,
} from "../../../../i18n/keysTranslations";
import { useTranslation } from "react-i18next";
import TooltipOptimization from "../../../Tooltip/TooltipOptimization";
import { useCallback, useEffect } from "react";
import _ from "lodash";
import {
  buildCreativeOptimizationPrompt,
  getCreativeOptimizationEntryForSlot,
} from "../../../../utils/creativeOptimizationContext";
import useApprovalFieldNavigateTab from "../../../../pages/NewAdsCampaign/hooks/useApprovalFieldNavigateTab";

const getFileNameFromUrl = (url = "") => {
  try {
    const uuid = getUUIDFromURL(url);
    return decodeURIComponent(uuid);
  } catch (e) {
    return "media";
  }
};

const SortableItem = ({
  id,
  index,
  url,
  isDisabled,
  basicPath,
  userID,
  onReplaceContentAt,
  onDeleteAt,
  getFileNameFromUrl,
  language,
  initialValuesAddContent = {},
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const onDeleteSource = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onDeleteAt(index);
  };

  const onEditSource = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  const rowFieldPath = `${basicPath}.mediaUrls.[${index}]`;

  return (
    <Box sx={{ mb: 1 }} id={rowFieldPath} data-field={rowFieldPath}>
      <ListItem
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        sx={{
          cursor: isDisabled ? "default" : isDragging ? "grabbing" : "grab",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: 2,
          bgcolor: "transparent",
          transition: "all 120ms ease",
          opacity: isDragging ? 0.8 : 1,
          transform: isDragging ? "scale(1.02)" : "scale(1)",
          boxShadow: isDragging ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
          zIndex: isDragging ? 1000 : "auto",
        }}
        secondaryAction={
          <Stack
            direction="row"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            sx={{
              backgroundColor: "background.paper",
            }}
          >
            <Box onClick={onEditSource}>
              <IconButtonAddContent
                id={`edit-carousel-media-${basicPath}-${index}`}
                mode="icon"
                disabled={isDisabled}
                acceptedFormats={TIKTOK_IMAGE_FORMATS}
                multiple={false}
                pathStorage={`adsCampaigns/${userID}/`}
                modeUpload="sync"
                onAddContent={(contents) => {
                  if (!contents?.length) return;
                  onReplaceContentAt(index, contents[0]);
                }}
                initialValues={{
                  language,
                  platform: TIKTOK,
                  ...initialValuesAddContent,
                }}
                icon={<PencilLine color="#000" />}
                sx={{
                  height: 40,
                  width: 40,
                }}
              />
            </Box>
            <IconButton
              disabled={isDisabled}
              onClick={onDeleteSource}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              sx={{ ml: 1 }}
            >
              <Trash2 color="#000" />
            </IconButton>
          </Stack>
        }
      >
        <ListItemAvatar sx={{ mr: 1, display: "flex", alignItems: "center" }}>
          <DragIndicatorIcon
            sx={{
              mr: 1,
              color: "text.secondary",
              cursor: isDisabled ? "default" : "grab",
            }}
          />
          <Avatar
            variant="square"
            sx={{ width: 56, height: 56, borderRadius: 1 }}
          >
            <CardMedia
              url={url}
              sx={{ width: 56, height: 56, objectFit: "cover" }}
              propsType={{ controls: false, autoplay: false }}
            />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={getFileNameFromUrl(url)}
          primaryTypographyProps={{
            noWrap: true,
            title: getFileNameFromUrl(url),
            sx: {
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            },
          }}
        />
      </ListItem>
    </Box>
  );
};

const CarouselImagesManager = ({
  basicPath,
  isDisabled = false,
  onGeneratePreview = () => {},
}) => {
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    getValues,
    creativeOptimizations,
    onRemoveCreativeOptimization,
  } = useFormContext();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());

  const mediaUrls = watch(`${basicPath}.mediaUrls`) || [];
  const mediaData = watch(`${basicPath}.mediaData`) || [];
  const hasItems = (mediaUrls || []).length > 0;
  const language = watch("language");

  const creativeEntry = getCreativeOptimizationEntryForSlot({
    creativeOptimizations,
    formPath: basicPath,
    field: "mediaUrls",
  });

  const currentCount = mediaUrls.length;
  const isLimitReached = currentCount >= MAX_CONTENTS_TIKTOK_CAROUSEL;

  const onNavigateToImageIndex = useCallback(
    (index) => {
      const element = document.getElementById(
        `${basicPath}.mediaUrls.[${index}]`
      );
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    [basicPath]
  );

  useApprovalFieldNavigateTab({
    formPath: basicPath,
    onSelectIndex: onNavigateToImageIndex,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onAddContents = (urlsOrContents = []) => {
    const currentUrls = getValues(`${basicPath}.mediaUrls`) || [];
    const currentData = getValues(`${basicPath}.mediaData`) || [];

    urlsOrContents.forEach((item) => {
      if (currentUrls.length >= MAX_CONTENTS_TIKTOK_CAROUSEL) {
        return;
      }

      const formatted = formatMediaUrl(item);
      currentUrls.push(formatted.url);
      currentData.push(formatted.mediaData);
    });

    setValue(`${basicPath}.mediaUrls`, currentUrls, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`${basicPath}.mediaData`, currentData, {
      shouldValidate: true,
      shouldDirty: true,
    });

    onGeneratePreview();
  };

  const onReplaceContentAt = (index, content) => {
    const formatted = formatMediaUrl(content);
    const currentUrls = [...(mediaUrls || [])];
    const currentData = [...(mediaData || [])];
    currentUrls[index] = formatted.url;
    currentData[index] = formatted.mediaData;
    setValue(`${basicPath}.mediaUrls`, currentUrls, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`${basicPath}.mediaData`, currentData, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const matchedReplace = getCreativeOptimizationEntryForSlot({
      creativeOptimizations,
      formPath: basicPath,
      field: "mediaUrls",
    });
    if (matchedReplace?.slotKey) {
      onRemoveCreativeOptimization(matchedReplace.slotKey);
    }

    onGeneratePreview();
  };

  const onDeleteAt = (index) => {
    setValue(
      `${basicPath}.mediaUrls`,
      (mediaUrls || []).filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );
    setValue(
      `${basicPath}.mediaData`,
      (mediaData || []).filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true }
    );

    const matchedDelete = getCreativeOptimizationEntryForSlot({
      creativeOptimizations,
      formPath: basicPath,
      field: "mediaUrls",
    });
    if (matchedDelete?.slotKey) {
      onRemoveCreativeOptimization(matchedDelete.slotKey);
    }

    onGeneratePreview();
  };

  const onDeleteAll = () => {
    setValue(`${basicPath}.mediaUrls`, [], {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`${basicPath}.mediaData`, [], {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (creativeEntry?.slotKey) {
      onRemoveCreativeOptimization(creativeEntry.slotKey);
    }

    onGeneratePreview();
  };

  const onDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = mediaUrls.findIndex((url) => url === active.id);
      const newIndex = mediaUrls.findIndex((url) => url === over.id);

      const newUrls = arrayMove(mediaUrls, oldIndex, newIndex);
      const newData = arrayMove(mediaData, oldIndex, newIndex);

      setValue(`${basicPath}.mediaUrls`, newUrls, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${basicPath}.mediaData`, newData, {
        shouldValidate: true,
        shouldDirty: true,
      });

      onGeneratePreview();
    }
  };

  useEffect(() => {
    if (!creativeEntry?.title) return;
    const element = document.getElementById(`${basicPath}.mediaUrls`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    // eslint-disable-next-line
  }, [creativeEntry?.slotKey, basicPath]);

  const showTooltip = !_.isEmpty(creativeEntry);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        id={`${basicPath}.mediaUrls`}
      >
        <TooltipOptimization
          isOpen={showTooltip}
          titleTooltip={creativeEntry?.title}
          actionTooltip={creativeEntry?.action}
        >
          <IconButtonAddContent
            id={`add-carousel-media-${basicPath}`}
            mode="button"
            disabled={isDisabled || isLimitReached}
            acceptedFormats={TIKTOK_IMAGE_FORMATS}
            multiple={true}
            pathStorage={`adsCampaigns/${userID}/`}
            modeUpload="sync"
            initialValues={{
              language,
              platform: TIKTOK,
              ...(creativeEntry
                ? { prompt: buildCreativeOptimizationPrompt(creativeEntry) }
                : {}),
            }}
            onAddContent={(contents) => {
              if (creativeEntry?.slotKey) {
                onRemoveCreativeOptimization(creativeEntry.slotKey);
              }
              onAddContents(contents);
            }}
            textButton={CAROUSEL_IMAGES_MANAGER_ADD_IMAGE}
            startIconButton={<Plus size={18} color="#000" />}
            color="secondary"
            sx={{
              width: "max-content",
            }}
          />
        </TooltipOptimization>
        <Button
          color="secondary"
          startIcon={<Trash2 size={18} color="#000" />}
          disabled={isDisabled || !hasItems}
          onClick={onDeleteAll}
        >
          {t(CAROUSEL_IMAGES_MANAGER_DELETE_ALL)}
        </Button>
      </Stack>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          maxHeight: 360,
          overflowY: "auto",
          position: "relative",
          userSelect: "none",
        }}
      >
        {!hasItems && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2">
              {t(CAROUSEL_IMAGES_MANAGER_NO_IMAGES)}
            </Typography>
          </Box>
        )}
        {hasItems && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={mediaUrls}
              strategy={verticalListSortingStrategy}
            >
              <List disablePadding>
                {(mediaUrls || []).map((url, index) => {
                  const rowCreativeEntry = getCreativeOptimizationEntryForSlot({
                    creativeOptimizations,
                    formPath: basicPath,
                    field: "mediaUrls",
                  });

                  return (
                    <SortableItem
                      key={`${url}-${index}`}
                      id={url}
                      index={index}
                      url={url}
                      isDisabled={isDisabled}
                      basicPath={basicPath}
                      userID={userID}
                      onReplaceContentAt={onReplaceContentAt}
                      onDeleteAt={onDeleteAt}
                      getFileNameFromUrl={getFileNameFromUrl}
                      language={language}
                      initialValuesAddContent={
                        rowCreativeEntry
                          ? {
                              prompt:
                                buildCreativeOptimizationPrompt(
                                  rowCreativeEntry
                                ),
                            }
                          : {}
                      }
                    />
                  );
                })}
              </List>
            </SortableContext>
          </DndContext>
        )}
      </Box>
    </Box>
  );
};

export default CarouselImagesManager;
